import React from 'react';
import { Link } from 'react-router-dom';

const CareersPage: React.FC = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">Join the BarnBills Team</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Help us revolutionize horse farm management. We're building the future of equestrian software 
            with a team that's passionate about horses and technology.
          </p>
          <div className="inline-flex items-center space-x-2 bg-gray-800/50 border border-gray-700 rounded-full px-4 py-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Not actively hiring full-time • Open to exceptional part-time talent</span>
          </div>
        </div>

        {/* Company Values */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Horse People First</h3>
            <p className="text-gray-400">We understand the equestrian world because we live it. Every decision is made with horse welfare and farm operations in mind.</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Innovation</h3>
            <p className="text-gray-400">We're not just building software, we're reimagining how horse farms can operate in the 21st century.</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Team Spirit</h3>
            <p className="text-gray-400">Like a good barn team, we support each other, celebrate wins together, and always have each other's backs.</p>
          </div>
        </div>

        {/* Current Status */}
        <div className="mb-16">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Our Current Hiring Status</h2>
            <p className="text-gray-400 max-w-3xl mx-auto mb-6">
              We're not actively hiring for full-time positions at the moment, but we're always interested in connecting with exceptional talent. 
              If you're the right person, we could discuss part-time opportunities for developers or account representatives.
            </p>
            <div className="bg-blue-600/10 border border-blue-600/20 rounded-lg p-4 inline-block">
              <p className="text-blue-400 font-medium">All positions are remote but require residence in Lexington, KY</p>
            </div>
          </div>
        </div>

        {/* Potential Opportunities */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Potential Part-Time Opportunities</h2>
          
          <div className="space-y-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 hover:border-gray-700 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Part-Time Developer</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      <span>Remote (Lexington, KY residents only)</span>
                    </span>
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>Part-time</span>
                    </span>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">Development</span>
                </div>
              </div>
              <p className="text-gray-400 mb-6">
                For the right candidate, we'd consider a part-time development role. You'd work on our modern web platform 
                using cutting-edge technologies to build solutions for the equestrian industry.
              </p>
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Required Skills:</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">React</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">TypeScript</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">JavaScript</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Backend-as-a-Service Platforms</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Cloud Platforms</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Serverless Environments</span>
                </div>
              </div>
              <Link 
                to="/contact" 
                className="inline-flex items-center brand-gradient text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Express Interest
              </Link>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 hover:border-gray-700 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Part-Time Account Representative</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      <span>Remote (Lexington, KY residents only)</span>
                    </span>
                    <span className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>Part-time</span>
                    </span>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm">Customer Success</span>
                </div>
              </div>
              <p className="text-gray-400 mb-6">
                Support our growing customer base and help horse farms get the most out of BarnBills. 
                Perfect for someone who thrives on building relationships and helping others succeed.
              </p>
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Required Skills:</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Strong People Skills</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Prompt Communication</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Industry Knowledge</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Detail-Focused</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Tech Proficient</span>
                  <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">Eager to Learn</span>
                </div>
              </div>
              <Link 
                to="/contact" 
                className="inline-flex items-center brand-gradient text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Express Interest
              </Link>
            </div>
          </div>
        </div>

        {/* Join Our Team CTA */}
        <div className="text-center">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <h3 className="text-2xl font-semibold text-white mb-4">Think You're the Right Fit?</h3>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              We're selective about who we work with, but if you're passionate about horses, technology, and live in Lexington, KY, 
              we'd love to hear from you. Tell us about yourself and how you could contribute to BarnBills.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center space-x-2 brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
            >
              <span>Get In Touch</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareersPage; 